import { DataPeriod } from '@prisma/client';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router-dom';
import { useGetProjects } from '../projects/projects.queries';
import { EmptyUserReport } from './components/empty-user-report';
import { ProjectUserSelector } from './components/project-user-selector';
import { ReportRangePicker } from './components/report-range-picker';
import { UserReportContent } from './components/user-report-content';
import { UserReportSkeleton } from './components/user-report-skeleton';
import { useUserReport } from './user-report.queries';
import {
  dateToIsoStringIgnoringTimezone,
  getDateFromParams,
  getRangeFromParams,
  toStartOfRange,
} from './utils/date-utils';

const UserReport: React.FC = () => {
  const { t } = useTranslation('reports');
  const { projectId, userId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const { data: projects } = useGetProjects();
  const project = projects?.find((p) => p.id === parseInt(projectId || ''));

  const selectedDate = getDateFromParams(searchParams);
  const selectedRange = getRangeFromParams(searchParams);

  const isProjectReport = userId === 'All';

  const { data, isLoading } = useUserReport({
    userId: isProjectReport ? 'All' : userId ? parseInt(userId, 10) : 0,
    date: selectedDate,
    period: selectedRange,
    projectId: projectId ? parseInt(projectId, 10) : undefined,
    enabled: !!userId && !!projectId,
  });

  if (project && !project.UserProject?.length) {
    return <EmptyUserReport />;
  }

  const handleDateChange = (date: Date) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('date', dateToIsoStringIgnoringTimezone(date));
    setSearchParams(newParams);
  };

  const handleRangeChange = (range: DataPeriod) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('range', range);
    newParams.set('date', dateToIsoStringIgnoringTimezone(toStartOfRange(selectedDate, range)));
    setSearchParams(newParams);
  };

  if (isLoading) {
    return (
      <UserReportSkeleton
        selectedRange={selectedRange}
        selectedDate={selectedDate}
        onRangeChange={handleRangeChange}
        onDateChange={handleDateChange}
      />
    );
  }

  return (
    <div className="h-[calc(100vh-64px)] flex flex-col">
      <div className="flex-none p-6 bg-card border-b">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold">
            {isProjectReport ? t('title.projectReport') : t('title.userReport')}
          </h1>
          <div className="flex items-center gap-4">
            <ProjectUserSelector currentProjectId={projectId} currentUserId={userId} />
            <ReportRangePicker
              selectedRange={selectedRange}
              onRangeChange={handleRangeChange}
              selectedDate={selectedDate}
              onDateChange={handleDateChange}
            />
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-auto custom-scrollbar">
        <UserReportContent
          data={data}
          isLoading={isLoading}
          selectedRange={selectedRange}
          isProjectReport={isProjectReport}
        />
      </div>
    </div>
  );
};

export default UserReport;
